import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import HondaLoader from '../../components/common/HondaLoader';
import Swal from 'sweetalert2';
import AccordionSection from '../../components/common/AccordionSection';
import { usePrendas } from '../../hooks/usePrendas';
import {
  getPrendasListing,
  setPrendasStatus
} from '../../services/prendasService';
import { sanitizeInput } from '../../utils/inputValidation';
import { getUserSession } from '../../utils/auth';

// ═══════════════════════════════════════════════════════════════
// HELPERS PARA DOCUMENTOS
// ═══════════════════════════════════════════════════════════════

const buildDocUrl = (doc) => {
  if (!doc) return null;

  if (doc.documentPath) {
    return doc.documentPath;
  }

  if (doc.documentUrl) {
    return doc.documentUrl;
  }

  if (doc.documentB64) {
    const mime = getDocumentMimeType(doc);

    return `data:${mime};base64,${doc.documentB64}`;
  }

  return null;
};

const getDocumentMimeType = (doc) => {
  if (!doc) return 'application/octet-stream';

  const name = (
    doc.documentName ||
    doc.fileName ||
    doc.documentPath ||
    ''
  ).toLowerCase();

  if (
    name.includes('.jpg') ||
    name.includes('.jpeg')
  ) {
    return 'image/jpeg';
  }

  if (name.includes('.png')) {
    return 'image/png';
  }

  if (name.includes('.gif')) {
    return 'image/gif';
  }

  if (name.includes('.webp')) {
    return 'image/webp';
  }

  if (name.includes('.pdf')) {
    return 'application/pdf';
  }

  return 'application/pdf';
};

const isImageDocument = (doc) => {
  const mime = getDocumentMimeType(doc);

  return mime.startsWith('image/');
};

const isPdfDocument = (doc) => {
  const mime = getDocumentMimeType(doc);

  return mime === 'application/pdf';
};

// ═══════════════════════════════════════════════════════════════
// FECHAS
// ═══════════════════════════════════════════════════════════════

const formatearFechaInput = (fecha) => {
  if (!fecha || fecha === 'N/A') return '';

  try {
    if (fecha.includes('T')) {
      fecha = fecha.split('T')[0];
    }

    if (fecha.includes('-')) {
      const partes = fecha.split('-');

      if (
        partes.length === 3 &&
        partes[0].length === 4
      ) {
        return fecha;
      }
    }

    return fecha;
  } catch {
    return fecha;
  }
};

const formatearFechaDisplay = (fecha) => {
  if (!fecha || fecha === 'N/A') return 'N/A';

  try {
    if (fecha.includes('T')) {
      fecha = fecha.split('T')[0];
    }

    if (fecha.includes('-')) {
      const partes = fecha.split('-');

      if (
        partes.length === 3 &&
        partes[0].length === 4
      ) {
        return `${partes[2]}-${partes[1]}-${partes[0]}`;
      }
    }

    return fecha;
  } catch {
    return fecha;
  }
};

// ═══════════════════════════════════════════════════════════════
// COMPONENTE
// ═══════════════════════════════════════════════════════════════

const EvaluarPrenda = ({
  user,
  onLogout,
  onNavigate
}) => {

  // ═══════════════════════════════════════════════════════════
  // LISTA
  // ═══════════════════════════════════════════════════════════

  const [prendas, setPrendas] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // ═══════════════════════════════════════════════════════════
  // EVALUACIÓN
  // ═══════════════════════════════════════════════════════════

  const [selectedPrenda, setSelectedPrenda] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const [modalAprobar, setModalAprobar] = useState(false);
  const [modalRechazar, setModalRechazar] = useState(false);

  const [motivoRechazo, setMotivoRechazo] = useState('');

  // Documentos recibidos desde el endpoint
  const [documentos, setDocumentos] = useState([]);

  // Documento actual que se está visualizando
  const [anexoActual, setAnexoActual] = useState(1);

  // URL temporal para visualizar documentos
  const [blobUrl, setBlobUrl] = useState(null);

  // Tipo del documento actual
  const [documentType, setDocumentType] = useState(null);

  const [formData, setFormData] = useState({
    id: 0,
    group: '',
    order: '',
    titular: '',
    ident: '',
    sellerHouseId: 0,
    sellerHouseName: '',
    createDate: '',
    recordDate: '',
    vehiculeTag: '',
    vehiculeSerial: '',
    registerName: '',
    status: '',
  });

  const {
    loadPrendaInfo,
    savePrendaData
  } = usePrendas();

  // ═══════════════════════════════════════════════════════════
  // CARGAR LISTA EN REVISION
  // ═══════════════════════════════════════════════════════════

  useEffect(() => {
    const loadList = async () => {
      try {
        setLoadingList(true);

        const data = await getPrendasListing('EN REVISION', 0);

        if (!data || data.length === 0) {
          setPrendas([]);

          Swal.fire({
            icon: 'info',
            title: 'Sin prendas pendientes',
            text: 'No hay prendas en revisión en este momento.',
            confirmButtonColor: '#ef4444'
          });

          return;
        }

        const mappedData = data.map((item, idx) => ({
          id:
            item.id ||
            idx + 1,

          group:
            item.group ||
            item.grupo ||
            'N/A',

          order:
            item.order ||
            item.orden ||
            'N/A',

          titular:
            item.titular ||
            item.mainName ||
            'N/A',

          ident:
            item.ident ||
            item.documentNumber ||
            'N/A',

          sellerHouse:
            item.sellerHouse?.ceName ||
            item.sellerHouseName ||
            item.concesionario ||
            'N/A',

          createDate:
            item.createDate ||
            'N/A',

          vehiculeTag:
            item.vehiculeTag ||
            'N/A',

          status:
            item.status ||
            'EN REVISION',
        }));

        setPrendas(mappedData);

      } catch (err) {
        console.error(
          'Error cargando prendas EN REVISION:',
          err
        );

        setPrendas([]);

      } finally {
        setLoadingList(false);
      }
    };

    loadList();
  }, []);

  // ═══════════════════════════════════════════════════════════
  // ABRIR EVALUACIÓN
  // ═══════════════════════════════════════════════════════════

  const handleEvaluar = useCallback(
    async (prenda) => {
      try {
        setLoadingData(true);

        setSelectedPrenda(prenda);

        let data = null;

        try {
          data = await loadPrendaInfo(
            prenda.group,
            prenda.order,
            'STORED'
          );
        } catch (error) {
          console.error(
            'Error obteniendo información de prenda:',
            error
          );
        }

        const source = data || prenda;

        setFormData({
          id:
            source.id ||
            prenda.id ||
            0,

          group:
            source.group ||
            prenda.group ||
            '',

          order:
            source.order ||
            prenda.order ||
            '',

          titular:
            source.titular ||
            prenda.titular ||
            '',

          ident:
            source.ident ||
            prenda.ident ||
            '',

          sellerHouseId:
            source.sellerHouse?.id ||
            0,

          sellerHouseName:
            source.sellerHouse?.ceName ||
            prenda.sellerHouse ||
            '',

          createDate:
            source.createDate ||
            prenda.createDate ||
            '',

          recordDate:
            formatearFechaInput(
              source.recordDate ||
              ''
            ),

          vehiculeTag:
            source.vehiculeTag ||
            prenda.vehiculeTag ||
            '',

          vehiculeSerial:
            source.vehiculeSerial ||
            '',

          registerName:
            source.registerName ||
            '',

          status:
            source.status ||
            prenda.status ||
            '',
        });

        if (
          data &&
          Array.isArray(data.documents)
        ) {
          console.log(
            'Documentos recibidos:',
            data.documents
          );

          setDocumentos(data.documents);
        } else {
          setDocumentos([]);
        }

        setAnexoActual(1);

      } catch (err) {
        console.error(
          'Error cargando prenda:',
          err
        );

        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo cargar la información de la prenda.',
          confirmButtonColor: '#ef4444'
        });

      } finally {
        setLoadingData(false);
      }
    },
    [loadPrendaInfo]
  );

  // ═══════════════════════════════════════════════════════════
  // CARGAR DOCUMENTO ACTUAL
  // ═══════════════════════════════════════════════════════════

  const documentoActual =
    documentos.length > 0
      ? documentos[anexoActual - 1]
      : null;

  useEffect(() => {

    setBlobUrl(null);
    setDocumentType(null);

    if (!documentoActual) {
      return;
    }

    const rawUrl =
      buildDocUrl(documentoActual);

    if (!rawUrl) {
      return;
    }

    const mimeType =
      getDocumentMimeType(documentoActual);

    setDocumentType(mimeType);

    if (rawUrl.startsWith('data:')) {
      setBlobUrl(rawUrl);
      return;
    }

    let objUrl = null;

    const controller =
      new AbortController();

    const loadDocument = async () => {
      try {

        const response = await fetch(
          rawUrl,
          {
            signal:
              controller.signal
          }
        );

        if (!response.ok) {
          throw new Error(
            `HTTP ${response.status}`
          );
        }

        const blob =
          await response.blob();

        const finalType =
          blob.type ||
          mimeType ||
          'application/octet-stream';

        const typedBlob =
          new Blob(
            [blob],
            {
              type: finalType
            }
          );

        objUrl =
          URL.createObjectURL(
            typedBlob
          );

        setBlobUrl(objUrl);

      } catch (error) {

        if (
          error.name ===
          'AbortError'
        ) {
          return;
        }

        console.error(
          'Error cargando documento:',
          error
        );

        setBlobUrl(rawUrl);
      }
    };

    loadDocument();

    return () => {
      controller.abort();

      if (objUrl) {
        URL.revokeObjectURL(
          objUrl
        );
      }
    };

  }, [
    documentoActual?.documentPath,
    documentoActual?.documentUrl,
    documentoActual?.documentB64,
    documentoActual?.documentName
  ]);

  // ═══════════════════════════════════════════════════════════
  // CAMBIOS EN FORMULARIO
  // ═══════════════════════════════════════════════════════════

  const handleChange = (e) => {
    const {
      name,
      value
    } = e.target;

    setFormData(
      (prev) => ({
        ...prev,
        [name]:
          sanitizeInput(value)
      })
    );
  };

  // ═══════════════════════════════════════════════════════════
  // GUARDAR
  // ═══════════════════════════════════════════════════════════

  const handleGuardar = async () => {

    if (
      !formData.titular ||
      !formData.ident ||
      !formData.vehiculeTag
    ) {

      Swal.fire({
        icon: 'warning',
        title: 'Campos requeridos',
        text: 'Complete los campos obligatorios: Titular, Identificación y Patente.',
        confirmButtonColor: '#ef4444'
      });

      return;
    }

    try {

      setGuardando(true);

      await savePrendaData({

        id:
          formData.id,

        group:
          formData.group,

        order:
          formData.order,

        titular:
          formData.titular,

        ident:
          formData.ident,

        sellerHouse: {
          id:
            formData.sellerHouseId,

          ceName:
            formData.sellerHouseName
        },

        createDate:
          formData.createDate,

        recordDate:
          formData.recordDate,

        vehiculeTag:
          formData.vehiculeTag.toUpperCase(),

        vehiculeSerial:
          formData.vehiculeSerial,

        registerName:
          formData.registerName,

        status:
          formData.status ||
          'EN REVISION',

        documents:
          documentos,

      });

    } catch (error) {

      console.error(
        'Error guardando prenda:',
        error
      );

    } finally {

      setGuardando(false);

    }
  };

  // ═══════════════════════════════════════════════════════════
  // APROBAR
  // ═══════════════════════════════════════════════════════════

  const handleAprobar = async () => {

    try {

      const session =
        getUserSession();

      const userID =
        session?.id ||
        0;

      await setPrendasStatus(
        formData.id,
        'APROBADO',
        'Prenda aprobada',
        userID
      );

      setModalAprobar(false);

      await Swal.fire({
        icon: 'success',
        title: 'Prenda Aprobada',
        text: 'La prenda fue aprobada exitosamente.',
        confirmButtonColor: '#22c55e',
        timer: 2000
      });

      setPrendas(
  prev =>
    prev.filter(
      p =>
        p.id !== formData.id
    )
);

setSelectedPrenda(null);
setDocumentos([]);
setBlobUrl(null);

// Ir directamente al Dashboard de Prendas
onNavigate('/PrendasDashboard');

    } catch (err) {

      setModalAprobar(false);

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message,
        confirmButtonColor: '#ef4444'
      });

    }
  };

  // ═══════════════════════════════════════════════════════════
  // RECHAZAR
  // ═══════════════════════════════════════════════════════════

  const handleRechazar = async () => {

    if (!motivoRechazo.trim()) {

      Swal.fire({
        icon: 'warning',
        title: 'Motivo requerido',
        text: 'Ingrese el motivo de rechazo.',
        confirmButtonColor: '#ef4444'
      });

      return;
    }

    try {

      const session =
        getUserSession();

      const userID =
        session?.id ||
        0;

      await setPrendasStatus(
        formData.id,
        'RECHAZADO',
        motivoRechazo,
        userID
      );

      setModalRechazar(false);
      setMotivoRechazo('');

      await Swal.fire({
        icon: 'info',
        title: 'Prenda Rechazada',
        text: 'La prenda fue rechazada.',
        confirmButtonColor: '#ef4444',
        timer: 2000
      });

      setPrendas(
  prev =>
    prev.filter(
      p =>
        p.id !== formData.id
    )
);

setSelectedPrenda(null);
setDocumentos([]);
setBlobUrl(null);

// Ir directamente al Dashboard de Prendas
onNavigate('/PrendasDashboard');

    } catch (err) {

      setModalRechazar(false);

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message,
        confirmButtonColor: '#ef4444'
      });

    }
  };

  // ═══════════════════════════════════════════════════════════
  // FILTRAR PRENDAS
  // ═══════════════════════════════════════════════════════════

  const filteredPrendas =
    prendas.filter(
      (p) => {

        const s =
          searchTerm.toLowerCase();

        return (

          String(p.group)
            .toLowerCase()
            .includes(s) ||

          String(p.order)
            .toLowerCase()
            .includes(s) ||

          String(p.titular)
            .toLowerCase()
            .includes(s) ||

          String(p.ident)
            .toLowerCase()
            .includes(s) ||

          String(p.sellerHouse)
            .toLowerCase()
            .includes(s) ||

          String(p.createDate)
            .toLowerCase()
            .includes(s)

        );
      }
    );

  // ═══════════════════════════════════════════════════════════
  // VISTA 1 — TABLA
  // ═══════════════════════════════════════════════════════════

  if (!selectedPrenda) {

    return (

      <MainLayout
        user={user}
        onLogout={onLogout}
        onNavigate={onNavigate}
      >

        <div className="max-w-full mx-auto px-3 py-4">

          <div className="mb-8">

            <div className="flex items-center gap-3 mb-2">

              <div className="w-1.5 h-8 gradient-red rounded-full shadow-glow-red"></div>

              <h2 className="text-2xl font-bold text-gradient">
                Evaluación de Prendas
              </h2>

            </div>

            <p className="text-gray-500 text-sm ml-5">
              Prendas pendientes de evaluación y aprobación
            </p>

          </div>

          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-6">

            <div className="relative">

              <span className="material-icons absolute left-3 top-2.5 text-gray-400 text-sm">
                search
              </span>

              <input
                type="text"
                placeholder="Buscar por grupo, orden, titular, DNI/CUIT, concesionario o F. Creación Prenda..."
                value={searchTerm}
                onChange={(e) =>
                  setSearchTerm(
                    sanitizeInput(
                      e.target.value
                    )
                  )
                }
                className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800 text-sm focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
              />

            </div>

          </div>

          {loadingList ? (

            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12">

              <HondaLoader
                message="Cargando prendas en revisión..."
                variant="inline"
                size="large"
                subtitle="Obteniendo prendas pendientes de evaluación"
              />

            </div>

          ) : (

            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">

              <div className="overflow-x-auto">

                <table className="min-w-full divide-y divide-gray-200">

                  <thead className="bg-gradient-to-r from-red-600 to-red-700">

                    <tr>

                      {[
                        'Grupo',
                        'Orden',
                        'Titular',
                        'DNI / CUIT',
                        'Concesionario',
                        'F. Creación Prenda'
                      ].map(
                        (h) => (

                          <th
                            key={h}
                            className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider"
                          >
                            {h}
                          </th>

                        )
                      )}

                      <th className="px-4 py-4 text-center text-xs font-bold text-white uppercase tracking-wider">
                        Acciones
                      </th>

                    </tr>

                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">

                    {filteredPrendas.length === 0 ? (

                      <tr>

                        <td
                          colSpan={7}
                          className="px-4 py-12 text-center text-gray-500"
                        >

                          <span className="material-icons text-4xl text-gray-300 block mb-2">
                            inventory_2
                          </span>

                          {searchTerm
                            ? 'No se encontraron resultados para la búsqueda'
                            : 'No hay prendas en revisión en este momento'}

                        </td>

                      </tr>

                    ) : (

                      filteredPrendas.map(
                        (prenda) => (

                          <tr
                            key={prenda.id}
                            className="hover:bg-gray-50 transition-colors"
                          >

                            <td className="px-4 py-3 text-sm text-gray-900">
                              {prenda.group}
                            </td>

                            <td className="px-4 py-3 text-sm text-gray-900">
                              {prenda.order}
                            </td>

                            <td className="px-4 py-3 text-sm text-gray-900">
                              {prenda.titular}
                            </td>

                            <td className="px-4 py-3 text-sm text-gray-900 font-mono">
                              {prenda.ident}
                            </td>

                            <td className="px-4 py-3 text-sm text-gray-900">
                              {prenda.sellerHouse}
                            </td>

                            <td className="px-4 py-3 text-sm text-gray-900">
                              {formatearFechaDisplay(
                                prenda.createDate
                              )}
                            </td>

                            <td className="px-4 py-3 text-center">

                              <button
                                onClick={() =>
                                  handleEvaluar(
                                    prenda
                                  )
                                }
                                className="inline-flex items-center justify-center w-8 h-8 rounded-full text-green-600 hover:bg-green-600 hover:text-white transition-colors"
                                title="Evaluar prenda"
                              >

                                <span className="material-icons text-xl">
                                  arrow_forward
                                </span>

                              </button>

                            </td>

                          </tr>

                        )
                      )

                    )}

                  </tbody>

                </table>

              </div>

              {filteredPrendas.length > 0 && (

                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">

                  Mostrando{' '}
                  {filteredPrendas.length}{' '}
                  de{' '}
                  {prendas.length}{' '}
                  prendas en revisión

                </div>

              )}

            </div>

          )}

        </div>

      </MainLayout>

    );

  }

  // ═══════════════════════════════════════════════════════════
  // VISTA 2 — LOADING
  // ═══════════════════════════════════════════════════════════

  if (loadingData) {

    return (

      <MainLayout
        user={user}
        onLogout={onLogout}
        onNavigate={onNavigate}
      >

        <div className="flex items-center justify-center h-screen">

          <HondaLoader
            message="Cargando prenda para evaluación..."
            variant="fullscreen"
            size="large"
          />

        </div>

      </MainLayout>

    );

  }

  // ═══════════════════════════════════════════════════════════
  // VISTA 3 — EVALUACIÓN
  // ═══════════════════════════════════════════════════════════

  return (

    <MainLayout
      user={user}
      onLogout={onLogout}
      onNavigate={onNavigate}
    >

      <div className="max-w-full mx-auto px-4 py-4">

        {/* TÍTULO */}

        <div className="mb-6">

          <div className="flex items-center gap-3 mb-2">

            <button
              onClick={() => {
                setSelectedPrenda(null);
                setDocumentos([]);
                setBlobUrl(null);
              }}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-600 transition-colors mr-1"
            >

              <span className="material-icons text-base">
                arrow_back
              </span>

            </button>

            <div className="w-1.5 h-8 gradient-red rounded-full shadow-glow-red"></div>

            <h2 className="text-2xl font-bold text-gradient">
              Evaluación de Prenda
            </h2>

          </div>

          <p className="text-gray-500 text-sm ml-10">
            Revise la documentación y evalúe la prenda
          </p>

        </div>

        <div className="flex flex-col lg:flex-row gap-4">

          {/* ═══════════════════════════════════════════════════ */}
          {/* COLUMNA IZQUIERDA — VISOR */}
          {/* ═══════════════════════════════════════════════════ */}

          <div className="flex-1 bg-white border-2 border-gray-800 rounded-xl shadow-lg overflow-hidden flex flex-col">

            <div className="h-2 bg-gray-800"></div>

            {/* HEADER DOCUMENTO */}

            <div className="bg-gray-800 px-3 py-2 border-b border-gray-700">

              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">

                <div className="flex items-center gap-4">

                  <p className="text-xs text-white">

                    <span className="font-bold">
                      Nombre del Anexo:
                    </span>{' '}

                    {documentoActual?.documentName ||
                      'Sin documento'}

                  </p>

                  <p className="text-xs text-white">

                    <span className="font-bold">
                      Anexo:
                    </span>{' '}

                    {documentos.length > 0
                      ? `${anexoActual} / ${documentos.length}`
                      : '0 / 0'}

                  </p>

                </div>

                <div className="flex gap-2">

                  <button
                    onClick={() => {

                      if (
                        anexoActual > 1
                      ) {
                        setAnexoActual(
                          anexoActual - 1
                        );
                      }

                    }}
                    disabled={
                      anexoActual === 1 ||
                      documentos.length === 0
                    }
                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md transition text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >

                    <span className="material-icons text-sm">
                      chevron_left
                    </span>

                    Anterior

                  </button>

                  <button
                    onClick={() => {

                      if (
                        anexoActual <
                        documentos.length
                      ) {
                        setAnexoActual(
                          anexoActual + 1
                        );
                      }

                    }}
                    disabled={
                      anexoActual === documentos.length ||
                      documentos.length === 0
                    }
                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md transition text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >

                    Siguiente

                    <span className="material-icons text-sm">
                      chevron_right
                    </span>

                  </button>

                </div>

              </div>

            </div>

            {/* VISOR */}

            <div
              className="flex-1 p-3 flex items-center justify-center bg-gray-100"
              style={{
                minHeight: '550px'
              }}
            >

              {documentos.length === 0 ? (

                <div className="text-center">

                  <span className="material-icons text-5xl text-gray-400 mb-2">
                    description
                  </span>

                  <p className="text-sm text-gray-500">
                    No hay documentos disponibles
                  </p>

                  <p className="text-xs text-gray-400 mt-1">
                    Esta prenda no tiene documentos adjuntos
                  </p>

                </div>

              ) : !blobUrl ? (

                <div className="text-center">

                  <span className="material-icons text-5xl text-gray-400 mb-2 animate-pulse">
                    pending
                  </span>

                  <p className="text-sm text-gray-500">
                    Cargando documento...
                  </p>

                  <p className="text-xs text-gray-400 mt-1">
                    {documentoActual?.documentName}
                  </p>

                </div>

              ) : isImageDocument(documentoActual) ? (

                <div className="w-full h-full flex flex-col items-center justify-center gap-3">

                  <div
                    className="w-full flex items-center justify-center bg-gray-200 rounded-lg overflow-auto"
                    style={{
                      minHeight: '520px',
                      maxHeight: '650px'
                    }}
                  >

                    <img
                      src={blobUrl}
                      alt={
                        documentoActual?.documentName ||
                        'Documento'
                      }
                      className="max-w-full max-h-[620px] object-contain"
                    />

                  </div>

                  <a
                    href={blobUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium transition flex items-center gap-2"
                  >

                    <span className="material-icons text-sm">
                      open_in_new
                    </span>

                    Abrir documento

                  </a>

                </div>

              ) : isPdfDocument(documentoActual) ? (

                <div className="w-full h-full flex flex-col gap-3">

                  <iframe
                    src={blobUrl}
                    title={
                      documentoActual?.documentName ||
                      'Documento PDF'
                    }
                    className="w-full rounded-lg border border-gray-300"
                    style={{
                      minHeight: '520px',
                      height: 'calc(100vh - 250px)'
                    }}
                  />
                  
                </div>

              ) : (

                <div className="text-center">

                  <span className="material-icons text-5xl text-gray-400 mb-2">
                    description
                  </span>

                  <p className="text-sm text-gray-500">
                    No se puede visualizar este tipo de documento
                  </p>

                  <a
                    href={blobUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium transition items-center gap-2"
                  >

                    <span className="material-icons text-sm">
                      open_in_new
                    </span>

                    Abrir documento

                  </a>

                </div>

              )}

            </div>

          </div>

          {/* ═══════════════════════════════════════════════════ */}
          {/* COLUMNA DERECHA */}
          {/* ═══════════════════════════════════════════════════ */}

          <div className="w-full lg:w-[35%] bg-white border-2 border-gray-800 rounded-xl shadow-lg overflow-hidden flex flex-col">

            <div className="h-2 bg-gray-800"></div>

            <div className="px-3 py-2 border-b border-gray-700 bg-gray-800 flex items-center justify-between">

              <p className="text-xs font-bold text-white">
                Prenda: {formData.group} / {formData.order}
              </p>

              <div className="flex gap-2">

                <button
                  onClick={() =>
                    setModalRechazar(true)
                  }
                  className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md transition text-xs font-medium shadow-sm"
                >
                  Rechazar
                </button>

                <button
                  onClick={() =>
                    setModalAprobar(true)
                  }
                  className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded-md transition text-xs font-medium shadow-sm"
                >
                  Aprobar
                </button>

              </div>

            </div>

            <div
              className="flex-1 overflow-y-auto"
              style={{
                maxHeight: '600px'
              }}
            >

              <AccordionSection
                title="IDENTIFICACIÓN"
                icon="inventory_2"
              >

                <div className="flex justify-between py-1 border-b border-gray-100">

                  <span className="text-xs text-gray-500">
                    Grupo
                  </span>

                  <span className="text-xs font-medium text-gray-900">
                    {formData.group}
                  </span>

                </div>

                <div className="flex justify-between py-1 border-b border-gray-100">

                  <span className="text-xs text-gray-500">
                    Orden
                  </span>

                  <span className="text-xs font-medium text-gray-900">
                    {formData.order}
                  </span>

                </div>

                <div className="flex justify-between py-1 border-b border-gray-100">

                  <span className="text-xs text-gray-500">
                    Nombre de Registro
                  </span>

                  <span className="text-xs text-gray-800 text-right">
                    {formData.registerName ||
                      'N/A'}
                  </span>

                </div>

                <div className="flex justify-between py-1 border-b border-gray-100">

                  <span className="text-xs text-gray-500">
                    F. Creación Prenda
                  </span>

                  <span className="text-xs text-gray-800">
                    {formatearFechaDisplay(
                      formData.createDate
                    )}
                  </span>

                </div>

                <div className="flex justify-between py-1">

                  <span className="text-xs text-gray-500">
                    Fecha de Registro
                  </span>

                  <span className="text-xs text-gray-800">
                    {formData.recordDate ||
                      'N/A'}
                  </span>

                </div>

              </AccordionSection>

              <AccordionSection
                title="DATOS DEL TITULAR"
                icon="person"
              >

                <div className="flex justify-between py-1 border-b border-gray-100">

                  <span className="text-xs text-gray-500">
                    Titular
                  </span>

                  <span className="text-xs font-medium text-gray-900 text-right">
                    {formData.titular ||
                      'N/A'}
                  </span>

                </div>

                <div className="flex justify-between py-1 border-b border-gray-100">

                  <span className="text-xs text-gray-500">
                    DNI / CUIT
                  </span>

                  <span className="text-xs text-gray-800 font-mono">
                    {formData.ident ||
                      'N/A'}
                  </span>

                </div>

                <div className="flex justify-between py-1">

                  <span className="text-xs text-gray-500">
                    Concesionario
                  </span>

                  <span className="text-xs text-gray-800 text-right">
                    {formData.sellerHouseName ||
                      'N/A'}
                  </span>

                </div>

              </AccordionSection>

              <AccordionSection
                title="DATOS DEL VEHÍCULO"
                icon="directions_car"
              >

                <div className="flex justify-between py-1 border-b border-gray-100">

                  <span className="text-xs text-gray-500">
                    Patente
                  </span>

                  <span className="text-xs text-gray-800 font-mono">
                    {formData.vehiculeTag ||
                      'N/A'}
                  </span>

                </div>

                <div className="flex justify-between py-1">

                  <span className="text-xs text-gray-500">
                    N° de Serie / Chasis
                  </span>

                  <span className="text-xs text-gray-800 font-mono">
                    {formData.vehiculeSerial ||
                      'N/A'}
                  </span>

                </div>

              </AccordionSection>

            </div>

          </div>

        </div>

      </div>

      {/* ═════════════════════════════════════════════════════ */}
      {/* MODAL APROBAR */}
      {/* ═════════════════════════════════════════════════════ */}

      {modalAprobar && (

        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">

          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">

            <div className="flex items-center gap-3 mb-4">

              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">

                <span className="material-icons text-green-600 text-2xl">
                  check_circle
                </span>

              </div>

              <div>

                <h3 className="text-lg font-bold text-gray-800">
                  Aprobar Prenda
                </h3>

                <p className="text-sm text-gray-500">
                  Confirme la aprobación
                </p>

              </div>

            </div>

            <p className="text-sm text-gray-600 mb-6">

              ¿Está seguro que desea aprobar la prenda de{' '}

              <strong>
                {formData.titular}
              </strong>

              ? Esta acción no se puede deshacer.

            </p>

            <div className="flex gap-3 justify-end">

              <button
                onClick={() =>
                  setModalAprobar(false)
                }
                className="px-4 py-2 text-sm border border-gray-300 rounded-xl text-gray-600 hover:bg-gray-50"
              >
                Cancelar
              </button>

              <button
                onClick={handleAprobar}
                className="px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-xl"
              >
                Confirmar Aprobación
              </button>

            </div>

          </div>

        </div>

      )}

      {/* ═════════════════════════════════════════════════════ */}
      {/* MODAL RECHAZAR */}
      {/* ═════════════════════════════════════════════════════ */}

      {modalRechazar && (

        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">

          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">

            <div className="flex items-center gap-3 mb-4">

              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">

                <span className="material-icons text-red-600 text-2xl">
                  cancel
                </span>

              </div>

              <div>

                <h3 className="text-lg font-bold text-gray-800">
                  Rechazar Prenda
                </h3>

                <p className="text-sm text-gray-500">
                  Ingrese el motivo de rechazo
                </p>

              </div>

            </div>

            <div className="mb-4">

              <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase">

                Motivo de Rechazo{' '}

                <span className="text-red-500">
                  *
                </span>

              </label>

              <textarea
                value={motivoRechazo}
                onChange={(e) =>
                  setMotivoRechazo(
                    sanitizeInput(
                      e.target.value
                    )
                  )
                }
                rows={3}
                placeholder="Describe el motivo del rechazo..."
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 resize-none"
              />

            </div>

            <div className="flex gap-3 justify-end">

              <button
                onClick={() => {
                  setModalRechazar(false);
                  setMotivoRechazo('');
                }}
                className="px-4 py-2 text-sm border border-gray-300 rounded-xl text-gray-600 hover:bg-gray-50"
              >
                Cancelar
              </button>

              <button
                onClick={handleRechazar}
                className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-xl"
              >
                Confirmar Rechazo
              </button>

            </div>

          </div>

        </div>

      )}

    </MainLayout>

  );
};

export default EvaluarPrenda;